import React, { useState, useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Card, CardHeader, CardBody, Col, Row, Button, Alert, Spinner } from "reactstrap";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";
import { FilterSection } from "./FilterSection";
import { MasterSheetTable } from "./MasterSheetTable";
import { PropertySelector } from "./PropertySelector";
import axios from "../../api/axios";
import { ENDPOINTS, getMastersheet, addTenant, updateTenant } from "./TenantForm/Helpers/api";
import { TenantForm } from "./TenantForm/TenantForm";
import { ReportModal } from "./MastersheetReport/ReportModal";

const MasterSheet = () => {
    const history = useHistory(); 

    useEffect(() => {
        const authUser = window.localStorage.getItem("authUser");
        const accessToken = window.localStorage.getItem("accessToken");

        if (!authUser || !accessToken) {
            history.replace("/login");
            return;
        }

        try {
            const parsedAuth = JSON.parse(authUser);
            // authUser is stored as the flat jwtInformation object; older/social logins
            // may nest it under `userData`. Support both, and only bounce to login when
            // there is no authenticated identity at all.
            const authData = parsedAuth?.userData || parsedAuth;
            if (!authData?.id) {
                window.localStorage.removeItem("authUser");
                window.localStorage.removeItem("accessToken");
                history.replace("/login");
            }
        } catch (error) {
            window.localStorage.removeItem("authUser");
            window.localStorage.removeItem("accessToken");
            history.replace("/login");
        }
    }, [history]);
    const parsedAuthUser = JSON.parse(window.localStorage.getItem("authUser") || '{}');
    const userData = parsedAuthUser.userData || parsedAuthUser;
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [masterSheetData, setMasterSheetData] = useState({ items: [] });
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [showForm, setShowForm] = useState(false);
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [currentProperty, setCurrentProperty] = useState(null);
    const [editMode, setEditMode] = useState(false);
    // Which Tenant Form section should start expanded (deep link from the notification center).
    const [initialSection, setInitialSection] = useState("");

    const [filterData, setFilterData] = useState({
        property_name: "",
        property_id: "",
        building_name: ""
    });

    const isValidSelection = !!filterData.property_id && (filterData.building_list?.length === 0 || !!filterData.building_name);

    // Utilities the selected building includes in rent (property `water_waste` maps to form `water`).
    // Null when no building/flags resolve, in which case TenantForm keeps its default behavior.
    const utilitiesAvailable = useMemo(() => {
        const buildings = filterData.building_list || [];
        const selected = filterData.building_name
            ? buildings.find((b) => b.buildingName === filterData.building_name)
            : buildings.length === 1
                ? buildings[0]
                : null;
        const ua = selected?.utilities_available;
        if (!ua) return null;
        return {
            heat: ua.heat === true,
            water: ua.water_waste === true,
            electricity: ua.electricity === true,
        };
    }, [filterData.building_list, filterData.building_name]);

    const [filterValues, setFilterValues] = useState({});

    const fetchMasterSheet = async () => {
        const { property_id, building_name } = filterData;
        if (!property_id) return;

        setIsLoading(true);
        try {
            const { data } = await getMastersheet({
                property_id,
                building_name,
                filters: filterValues // Use filterValues for actual filtering
            });
            setMasterSheetData(data);
        } catch (error) {
            const message = error?.response?.data?.message || "Failed to fetch Master Sheet.";
            if (message === 'Building name is required for this property') {
                return;
            }
            if (message === 'Master sheet not found for the given property') {
                setMasterSheetData({ items: [] });
            }

            Swal.fire("Error", message, "error");
        } finally {
            setIsLoading(false);
        }
    };

    // Depend on the filters' VALUE, not object identity: the reset effect below swaps
    // in a fresh {} on every property/building change, which used to refire this and
    // load the sheet twice per selection.
    const filterValuesKey = JSON.stringify(filterValues);
    useEffect(() => {
        if (!isValidSelection) return;
        fetchMasterSheet();
    }, [filterData.property_id, filterData.building_name, filterValuesKey]);

    // Selection changes come through here so stale filter values from the previous
    // property/building can never ride along into the single fetch above.
    const setFilterDataForSelection = (updater) => {
        setFilterData(updater);
        setFilterValues({});
    };

    const handleMasterSheetAction = async () => {
        try {
            const result = await Swal.fire({
                title: "Choose an Action",
                text: `Do you want to import a file to the ${filterData.property_name} Master Sheet or download the Master Sheet?`,
                icon: "question",
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: "Download Master Sheet",
                denyButtonText: "Import File",
                cancelButtonText: "Cancel",
            });

            if (result.isConfirmed) {
                const downloadConfirm = await Swal.fire({
                    title: "Are you sure?",
                    text: "Do you want to download the Master Sheet Excel file?",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Yes, download!",
                    cancelButtonText: "No, cancel",
                });

                if (downloadConfirm.isConfirmed) {
                    const response = await axios.post(
                        ENDPOINTS.DOWNLOAD_MASTERSHEET_ITEMS,
                        filterData,
                        { responseType: "blob" }
                    );

                    const file = new Blob([response.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    saveAs(file, `MasterSheet.xlsx`);
                    Swal.fire("Success!", "The Excel file has been downloaded.", "success");
                }
            } else if (result.isDenied) {
                const fileInput = document.createElement("input");
                fileInput.type = "file";
                fileInput.accept = ".xlsx, .xls";
                fileInput.style.display = "none";

                fileInput.addEventListener("change", async (event) => {
                    const file = event.target.files[0];
                    if (file) {
                        const formData = new FormData();
                        formData.append("file", file);
                        formData.append("property_id", filterData.property_id || "");
                        formData.append("building_name", filterData.building_name || "");

                        try {
                            setIsLoading(true);
                            const response = await axios.post(ENDPOINTS.UPLOAD_MASTERSHEET_ITEMS, formData, {
                                headers: { "Content-Type": "multipart/form-data" },
                                timeout: 30000,
                            });

                            if (response.data.success) {
                                Swal.fire("Success!", "The file has been imported successfully.", "success");
                                fetchMasterSheet();
                            } else {
                                const errorMessages = response.data.errors.join('<br>');
                                Swal.fire("Validation Errors", errorMessages, "error");
                            }
                        } catch (error) {
                            if (error.response && error.response.status === 400) {
                                const errorData = error.response.data;
                                if (errorData.errors && Array.isArray(errorData.errors)) {
                                    const errorMessages = errorData.errors.join('<br>');
                                    Swal.fire("Validation Errors", errorMessages, "error");
                                } else {
                                    Swal.fire("Import Error", errorData.message || "Invalid data in the uploaded file.", "error");
                                }
                            } else {
                                Swal.fire("Error", "An error occurred while importing the file.", "error");
                            }
                        } finally {
                            setIsLoading(false);
                        }
                    }
                });
                document.body.appendChild(fileInput);
                fileInput.click();
                document.body.removeChild(fileInput);
            }
        } catch (error) {
            Swal.fire("Error", "An unexpected error occurred.", "error");
        }
    };

    const handleExportMastersheetAction = async () => {
        try {
            const confirmExport = await Swal.fire({
                title: "Download Master Sheet",
                text: `Do you want to download the Master Sheet for ${filterData.property_name}?`,
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Yes, download it!",
                cancelButtonText: "Cancel",
            });

            if (confirmExport.isConfirmed) {
                const response = await axios.post(
                    ENDPOINTS.DOWNLOAD_MASTERSHEET_ITEMS,
                    filterData,
                    {
                        params: { mastersheet_id: masterSheetData._id },
                        responseType: "blob"
                    }
                );

                const file = new Blob([response.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

                saveAs(file, `MasterSheet.xlsx`);

                Swal.fire("Success!", "The Excel file has been downloaded.", "success");
            }
        } catch (error) {
            Swal.fire("Error", "An error occurred while exporting the Master Sheet.", "error");
        }
    };

    // Handle filter changes for FilterSection
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        console.log('Filter change:', name, value);
        setFilterValues((prev) => ({ ...prev, [name]: value }));
    };

    // ---- Deep link: /master_sheet?property_id&building_name&unit_no&section=pad ----
    // The notification center links straight to a unit's PAD Info; select the property /
    // building once the list is loaded, then open the tenant form once the items are loaded.
    const location = useLocation();
    const deepLink = useMemo(() => {
        const q = new URLSearchParams(location.search || "");
        if (!q.get("property_id")) return null;
        return {
            property_id: q.get("property_id"),
            building_name: q.get("building_name") || "",
            unit_no: q.get("unit_no") || "",
            section: q.get("section") || "",
        };
    }, [location.search]);
    const deepLinkDone = useRef({ key: "", property: false, unit: false });
    useEffect(() => {
        const key = location.search || "";
        if (deepLinkDone.current.key !== key) deepLinkDone.current = { key, property: false, unit: false };
    }, [location.search]);
    useEffect(() => {
        if (!deepLink || deepLinkDone.current.property) return;
        const list = filterData.propertyList || [];
        if (!list.length) return;
        deepLinkDone.current.property = true;
        const prop = list.find((pr) => String(pr._id) === String(deepLink.property_id));
        if (!prop) return;
        setFilterData((prev) => ({
            ...prev,
            property_id: prop._id,
            property_name: prop.title,
            building_list: prop.building || [],
            building_name: deepLink.building_name || "",
        }));
    }, [deepLink, filterData.propertyList]);
    useEffect(() => {
        if (!deepLink || !deepLink.unit_no || deepLinkDone.current.unit) return;
        if (String(filterData.property_id) !== String(deepLink.property_id)) return;
        if ((filterData.building_name || "") !== (deepLink.building_name || "")) return;
        const items = masterSheetData?.items || [];
        if (!items.length) return;
        deepLinkDone.current.unit = true;
        const want = deepLink.unit_no.trim().toLowerCase();
        const item = items.find((it) => String(it.unitNo ?? "").trim().toLowerCase() === want);
        if (!item) return;
        setInitialSection(deepLink.section === "pad" ? "pad" : "");
        setCurrentProperty({ ...item, propertyId: filterData.property_id, buildingName: filterData.building_name });
        setEditMode(true);
        setShowForm(true);
    }, [deepLink, masterSheetData, filterData.property_id, filterData.building_name]);

    // TenantForm handlers
    const handleAddTenant = () => {
        setCurrentProperty(null);
        setEditMode(false);
        setShowForm(true);
    };

    const handleEditTenant = (tenantData) => {
        setCurrentProperty(tenantData);
        setEditMode(true);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setCurrentProperty(null);
        setEditMode(false);
        setInitialSection("");
    };

    const handleGenerateReport = () => {
        setReportModalOpen(true);
    };

    // Saves the form and nothing else. A failure is thrown, never swallowed: the form shows
    // it and stays open on top of what the user typed, so the save that did not happen can
    // never read as one that did. TenantForm owns the dialogs and closes itself on success.
    const handleFormSubmit = async (formData) => {
        try {
            setIsLoading(true);
            const payload = {
                values: formData,
                property_id: filterData.property_id,
                building_name: filterData.building_name,
            };

            const response = editMode
                ? await updateTenant({ ...payload, _id: currentProperty._id })
                : await addTenant(payload);

            if (response?.status !== 200 && response?.status !== 201) {
                throw new Error(
                    response?.data?.message || `Failed to ${editMode ? "update" : "add"} tenant.`
                );
            }

            fetchMasterSheet();
        } catch (error) {
            const failure = new Error(
                error.response?.data?.message ||
                error.message ||
                `An unexpected error occurred while trying to ${editMode ? "update" : "add"} the tenant.`
            );
            // The server answers a rejected save with a per-field list; carry it through so
            // the form can show which fields it was.
            failure.details = (error.response?.data?.errors || [])
                .map((err) => err?.message)
                .filter(Boolean);
            throw failure;
        } finally {
            setIsLoading(false);
        }
    };


    const buildTenantInfoString = (tenantData = {}) => {
        const property = filterData?.property_name || "N/A";
        const unitNo = tenantData?.unitNo || "N/A";
        const address = filterData?.building_name || "N/A";
        const tenantName = tenantData?.tenants || tenantData?.tenant || "N/A";

        return `Property: ${property}, UnitNo.: ${unitNo}, Address: ${address}, Tenant Name: ${tenantName}`;
    };


    return (
        <div className="page-content">
            <div className="container-fluid">
                <TenantForm
                    show={showForm}
                    handleClose={handleCloseForm}
                    propertyId={filterData.property_id || ""}
                    propertyName={filterData.property_name}
                    buildingName={filterData.building_name}
                    onSubmit={handleFormSubmit}
                    editData={currentProperty}
                    editMode={editMode}
                    isCompanyUser={userData.role === 'company' ? true : false}
                    tenantInfo={currentProperty}
                    utilitiesAvailable={utilitiesAvailable}
                    userRole={userData.role || ""}
                    initialSection={initialSection}
                />
                {reportModalOpen && <ReportModal
                    isOpen={reportModalOpen}
                    toggle={() => setReportModalOpen(false)}
                    masterSheetData={masterSheetData}
                />}
                <Row>
                    <Col xl={12}>
                        <Card>
                            <CardHeader>
                                <PropertySelector
                                    isFilterOpen={isFilterOpen}
                                    setIsFilterOpen={setIsFilterOpen}
                                    action={handleMasterSheetAction}
                                    export={handleExportMastersheetAction}
                                    report={handleGenerateReport}
                                    userData={userData}
                                    filterData={filterData}
                                    setFilterData={setFilterDataForSelection}
                                    isMastersheetAvailable={{ isValidSelection, masterSheetData }}
                                    onAddTenant={handleAddTenant}
                                />
                                <FilterSection
                                    isFilterOpen={isFilterOpen}
                                    filterData={{
                                        ...filterData,
                                        ...filterValues // Merge both for FilterSection
                                    }}
                                    handleFilterChange={handleFilterChange}
                                />
                            </CardHeader>

                            {/* Loading State */}
                            {isLoading ? (
                                <CardBody className="text-center py-5">
                                    <Spinner color="primary" size="lg" className="mb-3" />
                                    <h5 className="text-muted">Loading...</h5>
                                </CardBody>
                            ) : (
                                <>
                                    {/* Alert Messages - Only show when not loading */}
                                    {!isValidSelection && (
                                        <Alert color="danger" className="mt-3 text-center">
                                            <strong>⚠️ Please select both a Property and its corresponding Building to proceed.</strong>
                                        </Alert>
                                    )}

                                    {isValidSelection && masterSheetData.items.length <= 0 && (
                                        <Alert color="warning" className="mt-3 text-center">
                                            <strong>⚠️ Master Sheet is configured, but no items are currently available.</strong>
                                            <p className="mb-0">
                                                Please ensure you have imported the template correctly. Items will appear here once data is properly uploaded.
                                            </p>
                                        </Alert>
                                    )}

                                    {isValidSelection && masterSheetData === undefined && (
                                        <>
                                            {!isCreatingNew ? (
                                                <Alert color="warning" className="d-flex align-items-center justify-content-between p-3 border border-warning rounded">
                                                    <div className="d-flex align-items-center">
                                                        <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                                                        <strong>⚠️ No Layout Available for this Property</strong>
                                                    </div>
                                                    <Button
                                                        color={isCreatingNew ? "secondary" : "primary"}
                                                        className="fw-bold px-4"
                                                        onClick={() => setIsCreatingNew(!isCreatingNew)}
                                                    >
                                                        <i className="bi bi-plus-lg me-1"></i> Create New Layout
                                                    </Button>
                                                </Alert>
                                            ) : (
                                                <MasterSheetTable config={masterSheetData || { items: [] }} />
                                            )}
                                        </>
                                    )}

                                    {isValidSelection && masterSheetData?.items?.length > 0 && (
                                        <MasterSheetTable
                                            mastersheet={masterSheetData}
                                            onEditTenant={handleEditTenant}
                                            propertyId={filterData.property_id}
                                            buildingName={filterData.building_name}
                                            onDataUpdate={fetchMasterSheet}
                                        />
                                    )}
                                </>
                            )}
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export { MasterSheet };